/**
 * Mixin for a field that supports loading its values asynchronously.
 */
import { AsyncableInputOptions, MultiValue } from '@/components/ui/DynamicTechTaskCard/types/input-field'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import requests, { Response } from '@/helpers/requests'
import debug from '@/helpers/debug'
import { InputField } from '@/components/ui/DynamicTechTaskCard/types'


interface LoadAsyncValuesResponse<V> extends Response {
    data: {
        asyncValues: V[];
    };
}


interface RequestPayload {
    [key: string]: unknown;
}


@Component
export default class AsyncValueMixin<O extends AsyncableInputOptions<V>, V extends MultiValue> extends Vue {
    @Prop({ required: true }) inputField!: InputField
    @Prop({ required: true }) disabled!: boolean
    @Prop({ required: true }) loading!: boolean
    loadingValues = false
    asyncValues: V[] = []

    get inputData(): { [key: string]: unknown } {
        return this.$store.state.techTask.inputData
    }

    get taskName(): string {
        return this.$store.state.techTask.taskName
    }

    get inputOptions(): O {
        return this.inputField.inputOptions as O
    }

    get isAsync(): boolean {
        return !!this.inputOptions.asyncLoad
    }

    get inputValues(): V[] {
        if (this.isAsync) {
            return this.asyncValues
        } else {
            return this.inputOptions.inputValues
        }
    }

    get asyncLoadUrl(): string {
        return `/support/${this.taskName}/load-values/${this.inputField.key}`
    }

    get loadOnValues(): RequestPayload {
        const loadOnValues: RequestPayload = {}
        for (const key of this.inputOptions?.asyncLoad.loadOn || []) {
            loadOnValues[key as string] = this.inputData[key] as unknown
        }
        return loadOnValues
    }

    async mounted() {
        await this.loadAsyncValues()
    }

    @Watch('inputData', { deep: true })
    async loadOnInputChange() {
        await this.loadAsyncValues()
    }

    @Watch('disabled')
    async loadOnEnable() {
        await this.loadAsyncValues()
    }

    async loadAsyncValues() {
        if (this.isAsync && !this.disabled) {
            try {
                this.loadingValues = true
                const response = await requests.post(this.asyncLoadUrl, this.loadOnValues) as LoadAsyncValuesResponse<V>
                this.asyncValues = response.data.asyncValues as V[]
            } catch(e) {
                debug.error(e)
            }
            this.loadingValues = false
        }
    }
}
